import * as React from 'react';

import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    IconButton,
    InputAdornment,
    InputLabel,
    ListItemText,
    Menu,
    MenuItem,
    Pagination,
    Select,
    Snackbar,
    TableContainer,
    TextField,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import FilterListIcon from '@mui/icons-material/FilterList';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import TableComponent from '../../../components/Generic/TableComponent';
import EmployeeStatCards from '../../../components/Generic/EmployeeStatCards';
import MobileStickyActionBar from '../../../components/Generic/mobile/MobileStickyActionBar';
import NewEmployeeActionButtonComponent from '../../../components/Generic/NewEmployeeActionButtonComponent';
import ConfirmDeleteActionDialog from '../../../components/Generic/dialogs/ConfirmDeleteActionDialog';

import AddNewEmployeeDialog from '../../../components/Generic/dialogs/AddNewEmployeeDialog';
import EditEmployeeDialog from '../../../components/Generic/dialogs/EditEmployeeDialog';
import EmployeeViewContractDialog from '../../../components/Generic/dialogs/EmployeeViewContractDialog';
import AddContractDialog from '../../../components/Generic/dialogs/AddContractDialog';
import UploadPreviewDialog from '../../../components/Generic/dialogs/UploadPreviewDialog';

import {
    DEFAULT_EMPLOYEE_COLUMNS,
    EMPLOYEE_COLUMN_DEFS,
    useEmployeeHandlers,
} from '../../../hooks/useEmployeeHandlers';
import type {EmployeeColumnKey} from '../../../hooks/useEmployeeHandlers';
import AgenticChat from '../../../components/Generic/AgenticChat';
import {CHAT_API_BASE_URL} from '../../../config/apiConfig';
import {useTranslation} from 'react-i18next';


export default function EmployeePage(): React.ReactElement {
    const {t} = useTranslation();
    const {
        token,
        employees,
        loading,
        error,
        selected,
        searchInput,
        setSearchInput,
        setSearchQuery,
        branchFilter,
        setBranchFilter,
        statusFilter,
        setStatusFilter,
        branchOptions,
        statusOptions,
        facetFilteredEmployees,
        currentPage,
        setCurrentPage,
        itemsPerPage,
        openDialog,
        openEditDialog,
        openViewDialog,
        openAddContractDialog,
        openConfirmDialog,
        confirmDialogMessage,
        showSuccessMessage,
        setShowSuccessMessage,
        successMessageText,
        selectedEmployeeContract,
        selectedEmployeeId,
        newEmployee,
        editingEmployee,
        newContract,
        contractsPage,
        contractsTotalRecords,
        handleConfirmDialogClose,
        handleConfirmDialogNo,
        handleConfirmDialogYes,
        handleSelectAllClick,
        handleClick,
        isSelected,
        handleEdit,
        handleDelete,
        handleView,
        handleCloseViewDialog,
        handleActivate,
        handleDeactivate,
        handleBulkDelete,
        handleOpenDialog,
        handleCloseDialog,
        handleFormChange,
        handleCheckboxChange,
        handleSubmitEmployee,
        handleCloseEditDialog,
        handleEditFormChange,
        handleEditCheckboxChange,
        handleUpdateEmployee,
        handleAddContract,
        handleCloseAddContractDialog,
        handleContractFormChange,
        handleSubmitContract,
        handleContractsPageChange,
        handleConfirmTerminate,
        downloadEmployees,
        downloading,
        contractSubmitError,
        addEmployeeSubmitError,
        editEmployeeSubmitError,
        uploading,
        uploadPreviewOpen,
        uploadedEmployees,
        handleUploadEmployees,
        handleUploadedEmployeeChange,
        handleCloseUploadPreview,
        handleSaveUploadedEmployees,
        uploadSaveLoading,
        uploadSaveError,
        uploadError,
        clearUploadError,
        downloadEmployeeTemplate,
        templateDownloading,
    } = useEmployeeHandlers();

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const [visibleColumns, setVisibleColumns] = React.useState<Record<EmployeeColumnKey, boolean>>(DEFAULT_EMPLOYEE_COLUMNS);
    const [columnsAnchorEl, setColumnsAnchorEl] = React.useState<null | HTMLElement>(null);
    const [filterAnchorEl, setFilterAnchorEl] = React.useState<null | HTMLElement>(null);
    const [moreAnchorEl, setMoreAnchorEl] = React.useState<null | HTMLElement>(null);
    const toggleColumn = (key: EmployeeColumnKey) => {
        setVisibleColumns((prev) => ({...prev, [key]: !prev[key]}));
    };

    return (
        <>
            {/* Success Message */}
            <Snackbar
                open={showSuccessMessage}
                autoHideDuration={3000}
                onClose={() => setShowSuccessMessage(false)}
                anchorOrigin={{vertical: 'top', horizontal: 'right'}}
            >
                <Alert severity="success" onClose={() => setShowSuccessMessage(false)} sx={{width: '100%'}}>
                    {successMessageText}
                </Alert>
            </Snackbar>
            {/* Upload error (row-level errors from POST /api/employees/upload) */}
            <Dialog open={!!uploadError} onClose={clearUploadError} fullWidth maxWidth="sm">
                <DialogTitle>{t('employee.upload.error.title')}</DialogTitle>
                <DialogContent dividers>
                    <Typography variant="body2" sx={{mb: uploadError?.errors.length ? 1.5 : 0}}>
                        {uploadError?.message}
                    </Typography>
                    {uploadError?.errors.map((e, i) => (
                        <Alert key={i} severity="error" sx={{mb: 1, wordBreak: 'break-word'}}>
                            {e.column
                                ? t('employee.upload.error.rowError', {row: e.row, column: e.column, message: e.message})
                                : t('employee.upload.error.rowOnly', {row: e.row, message: e.message})}
                        </Alert>
                    ))}
                </DialogContent>
                <DialogActions>
                    <Button onClick={clearUploadError}>{t('employee.upload.error.close')}</Button>
                </DialogActions>
            </Dialog>
            {/* Page Header: Title + Subtitle + Add Employee */}
            <Box sx={{
                width: '100%',
                mb: 3,
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'space-between',
                alignItems: {xs: 'flex-start', sm: 'center'},
                gap: 2,
            }}>
                <Box sx={{textAlign: 'left'}}>
                    <Typography variant="h5" sx={{fontWeight: 700, textAlign: 'left'}}>
                        {t('employee.title')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{textAlign: 'left'}}>
                        {t('employee.subtitle')}
                    </Typography>
                </Box>
                <Button
                    variant="contained"
                    color="secondary"
                    startIcon={<AddIcon/>}
                    onClick={handleOpenDialog}
                    sx={{borderRadius: 1, px: 3}}
                >
                    {isMobile ? t('employee.addEmployee.buttonShort') : t('employee.addEmployee.button')}
                </Button>
            </Box>

            {/* Summary Stat Cards */}
            <Box sx={{mb: 3}}>
                <EmployeeStatCards employees={employees} loading={loading}/>
            </Box>

            {isMobile ? (
                <>
                    {/* Search box */}
                    <Card variant="outlined" sx={{borderRadius: 1, mb: 1.5}}>
                        <CardContent sx={{p: 1.5, '&:last-child': {pb: 1.5}}}>
                            <TextField
                                fullWidth
                                size="small"
                                placeholder={t('employee.search.placeholder')}
                                variant="outlined"
                                value={searchInput}
                                onChange={(e) => setSearchInput(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon color="action"/>
                                        </InputAdornment>
                                    ),
                                    endAdornment: searchInput ? (
                                        <InputAdornment position="end">
                                            <IconButton size="small" onClick={() => {
                                                setSearchInput('');
                                                setSearchQuery('');
                                            }}>
                                                <ClearIcon fontSize="small"/>
                                            </IconButton>
                                        </InputAdornment>
                                    ) : null,
                                }}
                            />
                        </CardContent>
                    </Card>

                    {/* Filters + count + overflow actions */}
                    <Card variant="outlined" sx={{borderRadius: 1, mb: 2}}>
                        <CardContent sx={{
                            p: 1.5,
                            '&:last-child': {pb: 1.5},
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 1,
                        }}>
                            <Button
                                size="small"
                                variant="outlined"
                                startIcon={<FilterListIcon/>}
                                onClick={(e) => setFilterAnchorEl(e.currentTarget)}
                                sx={{flexShrink: 0}}
                            >
                                {t('employee.filters.button')}
                            </Button>
                            <Menu
                                anchorEl={filterAnchorEl}
                                open={Boolean(filterAnchorEl)}
                                onClose={() => setFilterAnchorEl(null)}
                            >
                                <Box sx={{p: 2, display: 'flex', flexDirection: 'column', gap: 2, minWidth: 220}}>
                                    <FormControl size="small" fullWidth>
                                        <InputLabel id="employee-branch-filter-mobile-label">{t('hr.jobs.table.header.branch')}</InputLabel>
                                        <Select
                                            labelId="employee-branch-filter-mobile-label"
                                            label={t('hr.jobs.table.header.branch')}
                                            value={branchFilter}
                                            onChange={(e) => setBranchFilter(e.target.value)}
                                        >
                                            <MenuItem value="">{t('employee.filters.all')}</MenuItem>
                                            {branchOptions.map((b) => (
                                                <MenuItem key={b} value={b}>{b}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    <FormControl size="small" fullWidth>
                                        <InputLabel id="employee-status-filter-mobile-label">{t('hr.branches.table.header.status')}</InputLabel>
                                        <Select
                                            labelId="employee-status-filter-mobile-label"
                                            label={t('hr.branches.table.header.status')}
                                            value={statusFilter}
                                            onChange={(e) => setStatusFilter(e.target.value)}
                                        >
                                            <MenuItem value="">{t('employee.filters.all')}</MenuItem>
                                            {statusOptions.map((s) => (
                                                <MenuItem key={s} value={s}>{s}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Box>
                            </Menu>

                            <Typography variant="body2" color="text.secondary" noWrap>
                                {t('employee.count', {count: facetFilteredEmployees.length})}
                            </Typography>

                            <IconButton size="small" onClick={(e) => setMoreAnchorEl(e.currentTarget)} sx={{flexShrink: 0}}>
                                <MoreVertIcon/>
                            </IconButton>
                            <Menu
                                anchorEl={moreAnchorEl}
                                open={Boolean(moreAnchorEl)}
                                onClose={() => setMoreAnchorEl(null)}
                            >
                                <MenuItem component="label" disabled={uploading}>
                                    {uploading ? (
                                        <CircularProgress size={16} sx={{mr: 1.5}}/>
                                    ) : (
                                        <FileUploadIcon fontSize="small" sx={{mr: 1.5}}/>
                                    )}
                                    {uploading ? t('common.uploading') : t('employee.table.uploadEmployees')}
                                    <input
                                        type="file"
                                        hidden
                                        accept=".xlsx,.xls,.csv"
                                        onChange={(e) => {
                                            const file = e.target.files?.[0];
                                            if (file) handleUploadEmployees(file);
                                            e.target.value = '';
                                            setMoreAnchorEl(null);
                                        }}
                                    />
                                </MenuItem>
                                <MenuItem
                                    onClick={() => {
                                        downloadEmployeeTemplate();
                                        setMoreAnchorEl(null);
                                    }}
                                    disabled={templateDownloading}
                                >
                                    {templateDownloading ? (
                                        <CircularProgress size={16} sx={{mr: 1.5}}/>
                                    ) : (
                                        <DescriptionOutlinedIcon fontSize="small" sx={{mr: 1.5}}/>
                                    )}
                                    {t('employee.table.downloadTemplate')}
                                </MenuItem>
                                <MenuItem
                                    onClick={() => {
                                        downloadEmployees();
                                        setMoreAnchorEl(null);
                                    }}
                                    disabled={downloading}
                                >
                                    {downloading ? (
                                        <CircularProgress size={16} sx={{mr: 1.5}}/>
                                    ) : (
                                        <FileDownloadIcon fontSize="small" sx={{mr: 1.5}}/>
                                    )}
                                    {downloading ? t('common.downloading') : t('common.actions.download')}
                                </MenuItem>
                            </Menu>
                        </CardContent>
                    </Card>

                    {selected.length > 0 && (
                        <Typography variant="body2" color="primary" fontWeight={600} sx={{mb: 1}}>
                            {t('employee.selectedCount', {count: selected.length})}
                        </Typography>
                    )}

                    <TableComponent
                        ariaLabel="employee table"
                        employees={facetFilteredEmployees}
                        loading={loading}
                        error={error}
                        selected={selected}
                        page={currentPage}
                        rowsPerPage={itemsPerPage}
                        searchQuery={searchInput}
                        visibleColumns={visibleColumns}
                        handleSelectAllClick={handleSelectAllClick}
                        handleClick={handleClick}
                        isSelected={isSelected}
                        handleEdit={handleEdit}
                        handleDelete={handleDelete}
                        handleView={handleView}
                        handleAddContract={handleAddContract}
                    />

                    {/* Pagination */}
                    <Box sx={{display: 'flex', justifyContent: 'center', mt: 3, mb: 2}}>
                        <Pagination
                            color="primary"
                            count={Math.ceil(facetFilteredEmployees.length / itemsPerPage)}
                            page={currentPage}
                            onChange={(event, value) => setCurrentPage(value)}
                        />
                    </Box>
                </>
            ) : (
                <Card variant="outlined" sx={{borderRadius: 1}}>
                    <CardContent>
                        <Typography variant="h6" sx={{fontWeight: 700, mb: 2, textAlign: 'left'}}>
                            {t('employee.list.title')}
                        </Typography>

                        {/* Toolbar: Search, Dega, Statusi, Zgjidh kolonat, Importo, Eksporto — all in one line */}
                        <Box sx={{
                            display: 'flex',
                            flexWrap: 'nowrap',
                            alignItems: 'center',
                            gap: 2,
                            mb: 2,
                            overflowX: 'auto',
                            pb: 1,
                        }}>
                            <Box sx={{flex: '1 1 260px', minWidth: 220}}>
                                <TextField
                                    fullWidth
                                    placeholder={t('employee.search.placeholder')}
                                    variant="outlined"
                                    value={searchInput}
                                    onChange={(e) => setSearchInput(e.target.value)}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon color="action"/>
                                            </InputAdornment>
                                        ),
                                        endAdornment: searchInput ? (
                                            <InputAdornment position="end">
                                                <IconButton size="small" onClick={() => {
                                                    setSearchInput('');
                                                    setSearchQuery('');
                                                }}>
                                                    <ClearIcon fontSize="small"/>
                                                </IconButton>
                                            </InputAdornment>
                                        ) : null,
                                    }}
                                />
                            </Box>

                            <FormControl size="small" sx={{minWidth: 160, flexShrink: 0}}>
                                <InputLabel id="employee-branch-filter-label">{t('hr.jobs.table.header.branch')}</InputLabel>
                                <Select
                                    labelId="employee-branch-filter-label"
                                    label={t('hr.jobs.table.header.branch')}
                                    value={branchFilter}
                                    onChange={(e) => setBranchFilter(e.target.value)}
                                >
                                    <MenuItem value="">{t('employee.filters.all')}</MenuItem>
                                    {branchOptions.map((b) => (
                                        <MenuItem key={b} value={b}>{b}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <FormControl size="small" sx={{minWidth: 160, flexShrink: 0}}>
                                <InputLabel id="employee-status-filter-label">{t('hr.branches.table.header.status')}</InputLabel>
                                <Select
                                    labelId="employee-status-filter-label"
                                    label={t('hr.branches.table.header.status')}
                                    value={statusFilter}
                                    onChange={(e) => setStatusFilter(e.target.value)}
                                >
                                    <MenuItem value="">{t('employee.filters.all')}</MenuItem>
                                    {statusOptions.map((s) => (
                                        <MenuItem key={s} value={s}>{s}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<ViewColumnIcon/>}
                                onClick={(e) => setColumnsAnchorEl(e.currentTarget)}
                                sx={{flexShrink: 0, whiteSpace: 'nowrap'}}
                            >
                                {t('employee.table.chooseColumns')}
                            </Button>
                            <Menu
                                anchorEl={columnsAnchorEl}
                                open={Boolean(columnsAnchorEl)}
                                onClose={() => setColumnsAnchorEl(null)}
                            >
                                {EMPLOYEE_COLUMN_DEFS.map((c) => (
                                    <MenuItem key={c.key} onClick={() => toggleColumn(c.key)} dense>
                                        <Checkbox size="small" checked={visibleColumns[c.key]} sx={{p: 0.5, mr: 1}}/>
                                        <ListItemText primary={t(c.labelKey)}/>
                                    </MenuItem>
                                ))}
                            </Menu>

                            <Button
                                variant="outlined"
                                size="small"
                                component="label"
                                disabled={uploading}
                                startIcon={uploading ? <CircularProgress size={16} color="inherit"/> : <FileUploadIcon/>}
                                sx={{flexShrink: 0, whiteSpace: 'nowrap'}}
                            >
                                {uploading ? t('common.uploading') : t('employee.table.uploadEmployees')}
                                <input
                                    type="file"
                                    hidden
                                    accept=".xlsx,.xls,.csv"
                                    onChange={(e) => {
                                        const file = e.target.files?.[0];
                                        if (file) handleUploadEmployees(file);
                                        e.target.value = '';
                                    }}
                                />
                            </Button>

                            <Button
                                variant="outlined"
                                size="small"
                                onClick={downloadEmployeeTemplate}
                                disabled={templateDownloading}
                                startIcon={templateDownloading ? <CircularProgress size={16} color="inherit"/> : <DescriptionOutlinedIcon/>}
                                sx={{flexShrink: 0, whiteSpace: 'nowrap'}}
                            >
                                {t('employee.table.downloadTemplate')}
                            </Button>

                            <Button
                                variant="outlined"
                                size="small"
                                onClick={downloadEmployees}
                                disabled={downloading}
                                startIcon={downloading ? <CircularProgress size={16} color="inherit"/> : <FileDownloadIcon/>}
                                sx={{flexShrink: 0, whiteSpace: 'nowrap'}}
                            >
                                {downloading ? t('common.downloading') : t('common.actions.download')}
                            </Button>
                        </Box>

                        {/* Row count and selection info */}
                        <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1}}>
                            <Typography variant="body2" color="text.secondary">
                                {t('employee.count', {count: facetFilteredEmployees.length})}
                            </Typography>
                            {selected.length > 0 && (
                                <Typography variant="body2" color="primary" fontWeight={600}>
                                    {t('employee.selectedCount', {count: selected.length})}
                                </Typography>
                            )}
                        </Box>

                        <TableContainer sx={{overflowX: 'auto'}}>
                            <TableComponent
                                sx={{minWidth: 650}}
                                ariaLabel="employee table"
                                employees={facetFilteredEmployees}
                                loading={loading}
                                error={error}
                                selected={selected}
                                page={currentPage}
                                rowsPerPage={itemsPerPage}
                                searchQuery={searchInput}
                                visibleColumns={visibleColumns}
                                handleSelectAllClick={handleSelectAllClick}
                                handleClick={handleClick}
                                isSelected={isSelected}
                                handleEdit={handleEdit}
                                handleDelete={handleDelete}
                                handleView={handleView}
                                handleAddContract={handleAddContract}
                            />
                        </TableContainer>

                        {/* Pagination */}
                        <Box sx={{display: 'flex', justifyContent: 'center', mt: 3}}>
                            <Pagination
                                color="primary"
                                count={Math.ceil(facetFilteredEmployees.length / itemsPerPage)}
                                page={currentPage}
                                onChange={(event, value) => setCurrentPage(value)}
                            />
                        </Box>
                    </CardContent>
                </Card>
            )}

            {/* Bulk Action Buttons */}
            {isMobile ? (
                <MobileStickyActionBar>
                    <NewEmployeeActionButtonComponent
                        fullWidth
                        selected={selected}
                        handleActivate={handleActivate}
                        handleDeactivate={handleDeactivate}
                        handleBulkDelete={handleBulkDelete}
                    />
                </MobileStickyActionBar>
            ) : (
                <Box sx={{
                    mt: 3,
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 2,
                    alignItems: 'center'
                }}>
                    <NewEmployeeActionButtonComponent
                        selected={selected}
                        handleActivate={handleActivate}
                        handleDeactivate={handleDeactivate}
                        handleBulkDelete={handleBulkDelete}
                    />
                </Box>
            )}

            {/* Add Employee Dialog */}
            <AddNewEmployeeDialog
                open={openDialog}
                newEmployee={newEmployee}
                onClose={handleCloseDialog}
                onSubmit={handleSubmitEmployee}
                handleFormChange={handleFormChange}
                handleCheckboxChange={handleCheckboxChange}
                backendError={addEmployeeSubmitError}
            />

            {/* Edit Employee Dialog */}
            <EditEmployeeDialog
                open={openEditDialog}
                employee={editingEmployee}
                onClose={handleCloseEditDialog}
                onSubmit={handleUpdateEmployee}
                handleFormChange={handleEditFormChange}
                handleCheckboxChange={handleEditCheckboxChange}
                backendError={editEmployeeSubmitError}
            />

            {/* Confirmation Dialog */}
            <ConfirmDeleteActionDialog
                open={openConfirmDialog}
                message={confirmDialogMessage}
                onClose={handleConfirmDialogClose}
                onNo={handleConfirmDialogNo}
                onYes={handleConfirmDialogYes}
            />

            {/* View Employee Contract Dialog */}
            <EmployeeViewContractDialog
                open={openViewDialog}
                selectedEmployeeContract={selectedEmployeeContract}
                contractsPage={contractsPage}
                contractsTotalRecords={contractsTotalRecords}
                userId={selectedEmployeeId || 0}
                onClose={handleCloseViewDialog}
                onContractsPageChange={handleContractsPageChange}
                onConfirmTerminate={handleConfirmTerminate}
            />

            {/* Add Contract Dialog */}
            <AddContractDialog
                open={openAddContractDialog}
                newContract={newContract}
                branchName={employees.find((e) => e.id === selectedEmployeeId)?.branch ?? ''}
                onClose={handleCloseAddContractDialog}
                onSubmit={handleSubmitContract}
                handleContractFormChange={handleContractFormChange}
                contractSubmitError={contractSubmitError}
            />

            {/* AI Chat Assistant */}
            <AgenticChat
                token={token}
                apiUrl={CHAT_API_BASE_URL}
                module="employee"
            />

            {/* Upload Preview Dialog */}
            <UploadPreviewDialog
                open={uploadPreviewOpen}
                employees={uploadedEmployees}
                onClose={handleCloseUploadPreview}
                onSave={handleSaveUploadedEmployees}
                onChange={handleUploadedEmployeeChange}
                saveLoading={uploadSaveLoading}
                saveError={uploadSaveError}
            />
        </>
    );
}
